import { AxiosError } from "axios";
import {
  BusyPeriodFormValues,
  OutOfOfficeFormMode,
  OutOfOfficeView,
  SupplierBusyPeriod,
} from "pages/out-of-office/out-of-office.typing";
import { toBusyPeriodPayload } from "pages/out-of-office/busy-period.mapper";
import {
  useBusyPeriodsQuery,
  useCreateBusyPeriodMutation,
  useDeleteBusyPeriodMutation,
  useUpdateBusyPeriodMutation,
} from "query/busy-period.query";
import { useEffect, useState } from "react";
import { formatServerError } from "utils/format";

const getApiErrorMessage = (error: AxiosError | null): string | null =>
  formatServerError(error, (response) => {
    const data = response.data;
    if (Array.isArray(data) && data.every((item) => typeof item === "string")) {
      return data.join(" ");
    }
    if (data && typeof data === "object" && "errors" in data) {
      const errors = (data as { errors?: unknown }).errors;
      if (Array.isArray(errors) && errors.every((item) => typeof item === "string")) {
        return errors.join(" ");
      }
    }
    if (typeof data === "string" && data.trim()) {
      return data;
    }
    return null;
  });

export const useOutOfOfficeModal = (isShown: boolean, onClose: () => void) => {
  const [view, setView] = useState<OutOfOfficeView>("list");
  const [formMode, setFormMode] = useState<OutOfOfficeFormMode>("create");
  const [editingPeriod, setEditingPeriod] = useState<SupplierBusyPeriod | undefined>();
  const [formApiError, setFormApiError] = useState<string | null>(null);

  const { data: periods = [], isLoading, error: listError, refetch } = useBusyPeriodsQuery(isShown);

  const createMutation = useCreateBusyPeriodMutation();
  const updateMutation = useUpdateBusyPeriodMutation();
  const deleteMutation = useDeleteBusyPeriodMutation();

  useEffect(() => {
    if (isShown) {
      setView("list");
      setFormMode("create");
      setEditingPeriod(undefined);
      setFormApiError(null);
    }
  }, [isShown]);

  const goToList = () => {
    setView("list");
    setEditingPeriod(undefined);
    setFormMode("create");
    setFormApiError(null);
  };

  const handleAdd = () => {
    setFormMode("create");
    setEditingPeriod(undefined);
    setFormApiError(null);
    setView("form");
  };

  const handleEdit = (period: SupplierBusyPeriod) => {
    setFormMode("edit");
    setEditingPeriod(period);
    setFormApiError(null);
    setView("form");
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteMutation.mutateAsync(id);
    } catch {
      // List error surface via mutation; keep list view usable
    }
  };

  const handleSubmit = async (values: BusyPeriodFormValues) => {
    if (!values.startDate || !values.endDate) {
      return;
    }

    setFormApiError(null);
    const payload = toBusyPeriodPayload(values);

    try {
      if (formMode === "edit" && editingPeriod) {
        await updateMutation.mutateAsync({ id: editingPeriod.id, payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      goToList();
    } catch (error) {
      setFormApiError(getApiErrorMessage(error as AxiosError));
    }
  };

  const handleClose = () => {
    goToList();
    onClose();
  };

  const listApiError =
    getApiErrorMessage(listError) ?? (deleteMutation.isError ? getApiErrorMessage(deleteMutation.error) : null);

  return {
    view,
    formMode,
    editingPeriod,
    periods,
    isLoading,
    listApiError,
    formApiError,
    isSubmitting: createMutation.isPending || updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    refetch,
    goToList,
    handleAdd,
    handleEdit,
    handleDelete,
    handleSubmit,
    handleClose,
  };
};
